/**
 * Single source of truth for production-process colors. Reused across:
 *   - Process badge (Process Detail header)
 *   - Stage indicator (Process Detail header)
 *   - "By Process" tab badges and matrix cells
 *
 * Color encodes meaning: a Knitting badge in the header and a Knitting cell
 * in the By Process matrix MUST paint with the same teal. Hardcoding a
 * process color in any consumer is
 * an anti-pattern; route everything through `PROCESS_COLORS[process]`.
 *
 * Note: this map covers the six real production processes only. RawMaterial
 * is conceptually a cost component (rendered as a column in the by-fabric
 * cost grid) and is never the subject of a Process Detail navigation, so it
 * intentionally has no entry here.
 */
import type { ProcessKey } from './cost-analysis.models';

/** The six production processes that have their own cost center on the wire. */
export type ProductionProcessKey = Exclude<ProcessKey, 'RawMaterial'>;

export interface ProcessColorTokens {
  /** Stage-segment fill, mini-bar, secondary fills. */
  light: string;
  /** Text-on-tint, primary bars, accent line, focus rings tied to the process. */
  dark: string;
  /** Badge background — light enough to read dark text on, ~12-14% alpha. */
  tint: string;
  /** Badge border — ~25-30% alpha of dark for a near-imperceptible outline. */
  border: string;
}

export const PROCESS_COLORS: Record<ProductionProcessKey, ProcessColorTokens> = {
  Knitting: {
    light: '#62B8B1',
    dark: '#2D514E',
    tint: 'rgba(98, 184, 177, 0.14)',
    border: 'rgba(45, 81, 78, 0.25)',
  },
  Greige: {
    light: '#909018',
    dark: '#625F02',
    tint: 'rgba(144, 144, 24, 0.12)',
    border: 'rgba(98, 95, 2, 0.25)',
  },
  Batching: {
    light: '#BD8D33',
    dark: '#AD7000',
    tint: 'rgba(189, 141, 51, 0.12)',
    border: 'rgba(173, 112, 0, 0.25)',
  },
  Dyeing: {
    light: '#3D7BAE',
    dark: '#2C4961',
    tint: 'rgba(61, 123, 174, 0.12)',
    border: 'rgba(44, 73, 97, 0.25)',
  },
  Finishing: {
    light: '#2D514E',
    dark: '#2D514E',
    tint: 'rgba(45, 81, 78, 0.12)',
    border: 'rgba(45, 81, 78, 0.30)',
  },
  Rolling: {
    light: '#AD7000',
    dark: '#AD7000',
    tint: 'rgba(173, 112, 0, 0.12)',
    border: 'rgba(173, 112, 0, 0.30)',
  },
};

/**
 * Production-flow order. Drives the Process Detail stage indicator
 * (current process highlighted, others muted) and any future flow-aware
 * visualization.
 */
export const PROCESS_FLOW: readonly ProductionProcessKey[] = ['Knitting', 'Greige', 'Batching', 'Dyeing', 'Finishing', 'Rolling'];

/** True when `process` is one of the six production processes — narrows
 *  out RawMaterial so the resulting key is safely indexable into PROCESS_COLORS. */
export function isProductionProcess(process: ProcessKey): process is ProductionProcessKey {
  return process !== 'RawMaterial';
}
